import { Entries, Entry, Scalars } from "@examspark/types";

// All these utils used to live in the barrel file, and were
// moved here to clean that up

export function assertNativeError(e: unknown): asserts e is Error {
  if (!(e instanceof Error)) throw e;
}

export function assertAppSyncError(e: any): asserts e is AppSyncErrors {
  if (!("errors" in e)) throw e;
}

export const isAppSyncError = (e: any): e is AppSyncErrors => "errors" in e;

export const hasConditionalCheckFailedException = (e: AppSyncErrors) => {
  return e.errors.find(
    (e) => e.errorType === "DynamoDB:ConditionalCheckFailedException",
  );
};

type AppSyncErrors = {
  errors: {
    message: string;
    errorType: string;
  }[];
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const matchInRange = (
  str: string,
  searchValue: string | RegExp,
  start: number,
  end?: number,
) => {
  const match = str.substring(start, end).match(searchValue);
  if (match?.index) {
    match.index += start;
  }
  return match;
};

export const replaceInRange = (
  str: string,
  searchValue: string | RegExp,
  replacer: string | ((substring: string, ...args: any[]) => string),
  start: number,
  end?: number,
) =>
  str.substring(0, start) +
  str
    .substring(start, end ?? str.length)
    .replace(searchValue, replacer as any) +
  str.substring(end ?? str.length);

export const replaceByMatch = <T>(
  arr: Array<T> | undefined,
  predicate: (item: T) => boolean,
  replacements: Array<T> | undefined,
) => {
  if (arr == null) return replacements;
  if (replacements == null) return [...arr];
  return arr.filter((item) => !predicate(item)).concat(replacements);
};

export function partitionBy<
  TElement,
  TPartition extends keyof any | undefined,
  TMapped = TElement,
>(
  arr: readonly TElement[],
  partitionFn: (item: TElement) => TPartition,
  mapFn?: (item: TElement) => TMapped,
) {
  const result = {} as Record<NonNullable<TPartition>, TMapped[]>;

  for (const item of arr) {
    const partitionKey = partitionFn(item);
    if (partitionKey === undefined) continue;
    if (!(partitionKey in result)) {
      result[partitionKey] = [];
    }

    if (mapFn != null) {
      result[partitionKey].push(mapFn(item));
    } else {
      // In this condition, TMapped and TElement will in practice be the same type
      result[partitionKey].push(item as unknown as TMapped);
    }
  }

  return result;
}

export function sortBy<TObject, TValue>(valueFn: (o: TObject) => TValue) {
  return (left: TObject, right: TObject) => {
    const leftValue = valueFn(left);
    const rightValue = valueFn(right);

    if (leftValue < rightValue) return -1;
    if (leftValue > rightValue) return 1;
    return 0;
  };
}

export function flattenBy<TObject>(
  arr: TObject[],
  childrenFn: (o: TObject) => TObject[] | undefined | null,
): TObject[] {
  const results: TObject[] = [];

  for (const o of arr) {
    results.push(o);
    const children = childrenFn(o);
    if (!children?.length) continue;

    results.push(...flattenBy(children, childrenFn));
  }

  return results;
}

/**
 * Helper functions that do nothing; for avoiding eslint empty method warnings
 * when you explicitly want an empty method.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-function
export const doNothing = () => {};
export const promiseToDoNothing = () => Promise.resolve();

export const intersperse = <TElement, TSeparator>(
  array: TElement[],
  separator: TSeparator | ((left: TElement, right: TElement) => TSeparator),
): Array<TElement | TSeparator> => {
  const results: Array<TElement | TSeparator> = [];
  for (const element of array) {
    if (results.length > 0) {
      results.push(
        separator instanceof Function
          ? separator(array[array.length - 1], element)
          : separator,
      );
    }
    results.push(element);
  }
  return results;
};

/**
 * type guard for truthy values. Primarily useful in a filter
 */
type Falsy = null | undefined | false | 0 | "";
export const truthy = <T>(value: T | Falsy): value is Exclude<T, Falsy> =>
  Boolean(value);

/** @deprecated use `common-util` method */
export const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * type guard for definiedness. Primarily useful in a filter
 *
 * @deprecated use `common-util` method
 */
export const exists = <T>(value: T | null | undefined): value is T =>
  value != null;

/**
 * Await the usual time it takes for a record to get indexed if you need to
 * fetch that data immediately after editing.
 *
 * @deprecated use `common-util` method
 */
export const delayForIndexing = (ms = 2000) => delay(ms);

export const ifNotResolvedImmediately = async <T>(
  promise: Promise<T>,
  callback: () => void,
  delay?: number,
) => {
  const timeout = setTimeout(callback, delay);
  const result = await promise;
  clearTimeout(timeout);
  return result;
};

export const pollFor = async <T>(
  generate: () => Promise<T>,
  validate?: (value: T) => boolean | undefined | Promise<boolean | undefined>,
  options?: {
    timeout?: number;
    interval?: number;
    onTimeout?: (value: T | undefined) => T | undefined;
  },
): Promise<T | undefined> => {
  validate = validate ?? Boolean;

  const start = Date.now();
  let elapsed = 0;
  let value: T | undefined;

  do {
    elapsed = Date.now() - start;
    value = await generate();

    if (validate(value)) return value;
    await delay(options?.interval ?? 2000);
  } while (options?.timeout == null || elapsed < options.timeout);

  return options?.onTimeout?.(value) ?? value;
};

export const chunk = <T>(array: T[], size: number) => {
  const chunks: Array<T[]> = [];

  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }

  return chunks;
};

export const ensureArray = <T>(arg: T | T[] | undefined | null) => {
  if (arg == null) return [];
  if (Array.isArray(arg)) return arg;
  return [arg];
};

/**
 * Split an array into n chunks as evenly as possible.
 * https://stackoverflow.com/questions/8188548/splitting-a-js-array-into-n-arrays
 */
export const autoChunk = <T>(a: T[], n: number, balanced?: boolean) => {
  if (n < 2) return [a];

  const len = a.length;
  const out = [];
  let i = 0;
  let size: number | undefined;

  if (len % n === 0) {
    size = Math.floor(len / n);
    while (i < len) {
      out.push(a.slice(i, (i += size)));
    }
  } else if (balanced) {
    while (i < len) {
      size = Math.ceil((len - i) / n--);
      out.push(a.slice(i, (i += size)));
    }
  } else {
    n--;
    size = Math.floor(len / n);
    if (len % size === 0) size--;
    while (i < size * n) {
      out.push(a.slice(i, (i += size)));
    }
    out.push(a.slice(size * n));
  }

  return out;
};

/**
 * Remove all null-ish values from an array.
 *
 * Almost all properties returned in GraphQL have the potential to be `null`,
 * even values inside an array. Iterating values can be really annoying when
 * having to always check for null inside each loop. This method gives us a
 * convenient way to pre-clean the array to make looping easier.
 */
export const filterMaybeArray = <T>(
  arr: Array<T | null | undefined> | null | undefined,
) => arr?.filter<T>(hasValue) ?? [];

export const hasValue = <T>(datum: T | null | undefined): datum is T => !!datum;

export function filterMatches(
  filter: string | null | undefined,
  caseInsensitive: boolean,
  ...texts: Array<string | null | undefined>
): boolean;
export function filterMatches(
  filter: string | null | undefined,
  ...texts: Array<string | null | undefined>
): boolean;
export function filterMatches(
  filter: string | null | undefined,
  textOrCaseSensitive: string | null | undefined | boolean,
  ...texts: Array<string | null | undefined>
) {
  if (filter == null || filter === "") return true;

  let caseSensitive = false;
  if (typeof textOrCaseSensitive === "boolean") {
    caseSensitive = textOrCaseSensitive;
  } else {
    texts.unshift(textOrCaseSensitive);
  }

  const normalize = (text: string) =>
    caseSensitive ? text : text.toLocaleLowerCase();

  for (const text of texts) {
    if (!text) continue;
    if (normalize(text).includes(normalize(filter))) return true;
  }

  return false;
}

export function undefinedIfEmpty(
  value: string | number | null | undefined,
): string | undefined;
export function undefinedIfEmpty<T>(
  value: T[] | null | undefined,
): T[] | undefined;
export function undefinedIfEmpty(
  value: any[] | string | number | null | undefined,
) {
  if (value == null) return undefined;
  if (Array.isArray(value) && value.length === 0) return undefined;
  if (value === "" || value === 0) return undefined;
  return value;
}

export const itemKey = ({ id, sk }: { id: string; sk: string }) =>
  `${id}/${sk}`;

export const collectAllPages = async <
  TData extends { nextToken?: string | null },
>(
  queryFn: (nextToken: string | undefined) => Promise<TData | undefined>,
  options?: {
    firstNextToken?: string;
  },
) => {
  const results: Array<TData> = [];

  const nextTokens: Array<string | null | undefined> = [];
  if (options?.firstNextToken != null) {
    nextTokens.push(options.firstNextToken);
  }

  do {
    if (nextTokens.length > 1 && nextTokens.at(-1) === nextTokens.at(-2)) {
      throw new Error("Infinite loop detected");
    }

    const result = await queryFn(nextTokens.at(-1) ?? undefined);

    if (result === undefined) break;

    results.push(result);

    nextTokens.push(result.nextToken);
  } while (nextTokens.at(-1) != null);

  return results;
};

export const formatBankId = (workspaceId: string, bankIdFragment: string) => {
  if (workspaceId.includes("/")) {
    throw new Error("Workspace ID cannot contain slashes");
  }
  if (bankIdFragment.includes("/")) {
    throw new Error("Bank ID fragment cannot contain slashes");
  }

  return [workspaceId, bankIdFragment].join("/");
};

export const decodeBase64 = (
  base64: string,
  encoding: BufferEncoding = "ascii",
) => {
  return Buffer.from(base64, "base64").toString(encoding);
};

export const encodeBase64 = (string: string) => {
  return Buffer.from(string).toString("base64");
};

export function jsonStringify(
  value: NonNullable<unknown>,
  space?: string | number,
): string;
export function jsonStringify(
  value: NonNullable<unknown> | undefined,
  space?: string | number,
): string | undefined;
export function jsonStringify(
  value: NonNullable<unknown> | undefined | null,
  space?: string | number,
): string | undefined | null;
export function jsonStringify(value: any, space?: string | number) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, space);
}

export function checkJson(value: string): string;
export function checkJson(value: string | undefined): string | undefined;
export function checkJson(
  value: string | undefined | null,
): string | undefined | null;
export function checkJson(
  value: string | undefined | null,
): string | undefined | null {
  if (value === undefined) return undefined;
  if (value === null) return null;
  return JSON.stringify(JSON.parse(value));
}

export function generateCode(
  length = 6,
  alphabet = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ",
) {
  return Array.from({ length })
    .map(() => alphabet[Math.floor(Math.random() * alphabet.length)])
    .join("");
}

/** Returns a date in YYYY-MM-DD format */
export const getDateTimestamp = (date?: string | number) => {
  return new Date(date ? date : Date.now()).toISOString().split("T")[0];
};

/**
 * Returns a date in YYYY-MM-DD format using the user's local timezone date. For
 * node environments, use regular `getDateTimestamp()` */
export const getDateTimestampLocal = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const dd = String(today.getDate()).padStart(2, "0");
  const formattedDate = `${yyyy}-${mm}-${dd}`;

  return formattedDate;
};

export const filterObject = <TObject extends object>(
  obj: TObject,
  predicate: (entry: Entry<TObject>) => boolean,
) => {
  return Object.fromEntries(
    (Object.entries(obj) as Entries<TObject>).filter(predicate),
  );
};

export class AutoMap<K, V> extends Map<K, V> {
  constructor(private itemDefault: V | ((key: K) => V)) {
    super();
  }

  private getDefault(key: K) {
    if (typeof this.itemDefault === "function") {
      return (this.itemDefault as (key: K) => V)(key);
    }
    return this.itemDefault;
  }

  override get(key: K): V {
    const value = super.get(key) ?? this.getDefault(key);
    super.set(key, value);
    return value;
  }

  update(key: K, updater: (previousValue: V) => V) {
    const previousValue = this.get(key);
    this.set(key, updater(previousValue));
  }
}

export function getOrCreate<K extends string | number | symbol, V>(
  obj: Record<K, V> | Map<K, V>,
  key: K,
  valueFn: () => V,
): V {
  if (obj instanceof Map) {
    return getOrCreateFromMap(obj, key, valueFn);
  }

  return getOrCreateFromRecord(obj, key, valueFn);
}

export function getOrCreateFromRecord<K extends string | number | symbol, V>(
  record: Record<K, V>,
  key: K,
  valueFn: () => V,
): V {
  if (!(key in record)) {
    const value = valueFn();
    record[key] = value;
    return value;
  }
  return record[key];
}

export function getOrCreateFromMap<K, V>(
  map: Map<K, V>,
  key: K,
  valueFn: () => V,
): V {
  if (!map.has(key)) {
    const value = valueFn();
    map.set(key, value);
    return value;
  }

  return map.get(key)!;
}

export const createIdFromName = async (
  name: string,
  options?: {
    prefix?: string;
    usedIds?: Set<string>;
    checkIdUsed?: (id: string) => Promise<boolean>;
  },
) => {
  const maxAttempts = 20;

  const cleanedName = name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .replace(/ /g, "")
    .replace(/isd/g, "")
    .substring(0, 10)
    .trim();

  const bannedNumbers = [13, 66, 69, 96, 99];
  let randomNumber: number;

  for (let i = 0; i < maxAttempts; i++) {
    do {
      randomNumber = Math.floor(10 + Math.random() * 90);
    } while (bannedNumbers.includes(randomNumber));

    let id = `${cleanedName}${randomNumber}`;
    if (options?.prefix != null) {
      id = `${options.prefix}-${id}`;
    }

    if (options?.usedIds?.has(id)) {
      continue;
    }

    if (await options?.checkIdUsed?.(id)) {
      options?.usedIds?.add(id);
      continue;
    }

    return id;
  }

  throw new Error(
    `Could not generate a unique ID after ${maxAttempts} attempts`,
  );
};

export class ErrorWithCode extends Error {
  constructor(
    public readonly code: "NOT_ALLOWED" | "NOT_VALID",
    message?: string,
    public readonly data?: Record<string, Scalars>,
  ) {
    super(message);
  }
}
